import { Stack, Tabs, Typography } from "@mui/material";
import { employeeBussiness } from "api/bussiness/employee/employee-bussiness";
import { EmployeePermission } from "commons/enum/permission";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  EditButton,
  GoBackIconButton,
  RemoveButton,
} from "share-components/mui-wrap/button/action-button";
import { XTab } from "share-components/mui-wrap/tab/x-tab";
import { PageContentScrollableContainer } from "share-components/templates/page/page-content-container";
import { PageDetailContainer } from "share-components/templates/page/page-detail-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";
import { EditEmployee } from "../edit-employee/edit-employee";
import { UpdateExtraInfo } from "../update-extra-info/update-extra-info";
import { UpdateContractInsurance } from "../update-insurance-contractor/update-contract-insurance";
import { EmployeeBasicTable } from "./employee-basic";
import { ExtraInfoTable } from "./extra-information";
import { LaborContractInformationTable } from "./labor-contract-information";

type Tab = "employee" | "extra-info" | "labor-contract";

export const EmployeeDetail = observer(() => {
  const {
    employeeManagement: {
      getEmployeeDetail,
      openEdit,
      openUpdateLaborContract,
      openUpdateExtraInfo,
    },
  } = useStores();
  const [tab, setTab] = useState<Tab>("employee");

  const { employeeId } = useParams();
  useEffect(() => {
    if (employeeId) {
      getEmployeeDetail(employeeId);
    }
  }, [getEmployeeDetail, employeeId]);
  return (
    <PageDetailContainer>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header tabSelected={tab}></Header>
      </PageHeaderContainer>
      <Tabs
        value={tab}
        onChange={(_, value) => {
          setTab(value);
        }}
      >
        <XTab label="Thông tin cơ bản" value={"employee"} />
        <XTab label="Thông tin khác" value={"extra-info"} />
        <XTab label="Hợp đồng lao động và bảo hiểm" value={"labor-contract"} />
      </Tabs>
      <PageContentScrollableContainer>
        {tab === "employee" && <EmployeeBasicTable></EmployeeBasicTable>}
        {tab === "extra-info" && <ExtraInfoTable></ExtraInfoTable>}
        {tab === "labor-contract" && (
          <LaborContractInformationTable></LaborContractInformationTable>
        )}
      </PageContentScrollableContainer>

      <>
        {openEdit && <EditEmployee></EditEmployee>}
        {openUpdateLaborContract && (
          <UpdateContractInsurance></UpdateContractInsurance>
        )}
        {openUpdateExtraInfo && <UpdateExtraInfo></UpdateExtraInfo>}
      </>
    </PageDetailContainer>
  );
});
type HeaderProps = {
  tabSelected: Tab;
};
const Header = observer(({ tabSelected = "employee" }: HeaderProps) => {
  const {
    employeeManagement: { setProp: setEmployeeProps, employeeDetail },
    deleteConfirmationStore: { setProp, setDeletionCallback },
  } = useStores();
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const onDelete = useCallback(
    async (id: string) => {
      if (id) {
        await employeeBussiness.delete(id);
        goBack();
      }
    },
    [goBack]
  );
  const removeHandler = useCallback(() => {
    setProp("open", true);
    setDeletionCallback(() => {
      if (!employeeDetail?.id) return;
      onDelete(employeeDetail?.id);
    });
  }, [onDelete, employeeDetail?.id, setDeletionCallback, setProp]);

  const editHandler = useCallback(() => {
    setEmployeeProps("openEdit", true);
  }, [setEmployeeProps]);

  const onUpdateLaborContractClickHandler = useCallback(() => {
    setEmployeeProps("openUpdateLaborContract", true);
  }, [setEmployeeProps]);

  const onUpdateExtraInfoClickHandler = useCallback(() => {
    setEmployeeProps("openUpdateExtraInfo", true);
  }, [setEmployeeProps]);

  return (
    <>
      <Stack
        direction={"row"}
        justifyItems={"center"}
        spacing={1}
        alignItems={"center"}
      >
        <GoBackIconButton onClick={goBack}></GoBackIconButton>
        <Typography variant="subtitle2">{`Thông tin chi tiết `}</Typography>
      </Stack>
      <Stack direction={"row"} justifyItems={"center"} spacing={1}>
        {tabSelected === "employee" && (
          <>
            <RemoveButton
              accessPermission={[EmployeePermission.Delete]}
              onClick={removeHandler}
            ></RemoveButton>

            <EditButton
              accessPermission={[EmployeePermission.Update]}
              onClick={editHandler}
            ></EditButton>
          </>
        )}
        {tabSelected === "labor-contract" && (
          <>
            <EditButton
              accessPermission={[EmployeePermission.UpdateContractInsurance]}
              onClick={onUpdateLaborContractClickHandler}
            >
              Cập nhật hợp đồng lao động
            </EditButton>
          </>
        )}
        {tabSelected === "extra-info" && (
          <>
            <EditButton
              accessPermission={[EmployeePermission.UpdateInformation]}
              onClick={onUpdateExtraInfoClickHandler}
            >
              Cập nhật thông tin khác
            </EditButton>
          </>
        )}
      </Stack>
    </>
  );
});
